<!--
DashboardHero

Top-of-page dashboard summary that adapts its copy to the resolved workspace
role. Owners continue to see commercial framing, while team leaders and
team members receive operational or read-only wording without changing the
permission source of truth.

Permission rules:
- dashboardRole is used for layout/copy selection.
- summary.permissions gates upload, billing, and team actions.

@author Williams Francis <w.francis@nationaldrones.com> | <olawalewilliams9438@gmail.com>
-->
<template>
    <section class="workspace-dashboard__hero" data-home-tour-dashboard>
        <div class="workspace-dashboard__hero-copy">
            <div class="workspace-dashboard__hero-intro">
                <div class="workspace-dashboard__hero-topline">
                    <div class="workspace-dashboard__hero-teammark">
                        <v-icon size="22">$vuetify.icons.teams</v-icon>
                    </div>
                    <div class="workspace-dashboard__eyebrow">
                        <span>{{ summary.workspace?.name || 'Operations Team' }}</span>
                        <span class="workspace-dashboard__separator">•</span>
                        <span>{{ summary.userName || 'Williams' }}</span>
                    </div>
                </div>

                <h1>
                    Welcome back, {{ summary.userName || 'Williams' }}
                    <span class="workspace-dashboard__hero-wave" aria-hidden="true">👋</span>
                </h1>

                <div class="workspace-dashboard__hero-badges">
                    <span class="workspace-dashboard__hero-badge workspace-dashboard__hero-badge--primary">
                        <v-icon size="15">$vuetify.icons.teams</v-icon>
                        <strong>{{ summary.workspace?.role || 'Access Member' }}</strong>
                    </span>
                    <span v-if="showPlanBadge" class="workspace-dashboard__hero-badge workspace-dashboard__hero-badge--success">
                        <v-icon size="15">$vuetify.icons.billing</v-icon>
                        <strong>{{ summary.plan?.name || 'BYO Dataset Pass' }}</strong>
                    </span>
                    <span v-if="showPlanBadge" class="workspace-dashboard__hero-badge">
                        <strong>{{ summary.plan?.priceLabel || 'AUD 149' }}</strong>
                    </span>
                    <span class="workspace-dashboard__hero-badge">
                        <v-icon size="15">$vuetify.icons.teams</v-icon>
                        <strong>{{ myTeamCountLabel }}</strong>
                    </span>
                </div>

                <div class="workspace-dashboard__hero-actions">
                    <v-btn
                        v-for="action in heroActions"
                        :key="action.label"
                        :class="[
                            'workspace-dashboard__hero-action',
                            { 'workspace-dashboard__hero-action--primary': action.primary },
                        ]"
                        :color="action.primary ? 'primary' : undefined"
                        :variant="action.primary ? undefined : 'outlined'"
                        :href="action.href"
                    >
                        <v-icon start>{{ action.icon }}</v-icon>
                        {{ action.label }}
                    </v-btn>
                </div>
            </div>

            <div class="workspace-dashboard__hero-details" aria-label="Workspace summary">
                <article class="workspace-dashboard__hero-detail">
                    <span>Workspace</span>
                    <strong>{{ summary.workspace?.name || 'Operations Team' }}</strong>
                    <p>Your active workspace</p>
                </article>

                <article class="workspace-dashboard__hero-detail">
                    <span>Current plan</span>
                    <strong>{{ detailPlanLabel }}</strong>
                    <p>{{ detailPlanCopy }}</p>
                </article>
            </div>
        </div>

        <div class="workspace-dashboard__hero-visual" :style="heroVisualStyle">
            <div class="workspace-dashboard__hero-visual-overlay">
                <div class="workspace-dashboard__hero-chip">
                    <v-icon size="16">{{ chipIcon }}</v-icon>
                    <span>{{ planLabel }}</span>
                </div>

                <div class="workspace-dashboard__hero-glance">
                    <article class="workspace-dashboard__hero-glance-card">
                        <div class="workspace-dashboard__hero-glance-icon workspace-dashboard__hero-glance-icon--blue">
                            <v-icon size="22">mdi-database</v-icon>
                        </div>
                        <div>
                            <span title="Published datasets contained within Dataset Groups.">Datasets</span>
                            <strong>{{ heroCount(summary.workspace?.datasetCount) }}</strong>
                            <p>{{ datasetDetail }}</p>
                        </div>
                    </article>

                    <article class="workspace-dashboard__hero-glance-card">
                        <div class="workspace-dashboard__hero-glance-icon workspace-dashboard__hero-glance-icon--green">
                            <v-icon size="22">mdi-harddisk</v-icon>
                        </div>
                        <div>
                            <span>{{ secondaryMetricLabel }}</span>
                            <strong>{{ secondaryMetricValue }}</strong>
                            <p>{{ storageDetail }}</p>
                        </div>
                    </article>

                    <article class="workspace-dashboard__hero-glance-card">
                        <div class="workspace-dashboard__hero-glance-icon workspace-dashboard__hero-glance-icon--purple">
                            <v-icon size="22">$vuetify.icons.teams</v-icon>
                        </div>
                        <div>
                            <span>{{ teamMetricLabel }}</span>
                            <strong>{{ heroCount(teamMetricValue) }}</strong>
                            <p>{{ accessTeamDetail }}</p>
                            <small>{{ billingTeamDetail }}</small>
                        </div>
                    </article>

                    <article class="workspace-dashboard__hero-glance-card">
                        <div class="workspace-dashboard__hero-glance-icon workspace-dashboard__hero-glance-icon--orange">
                            <v-icon size="22">$vuetify.icons.cube</v-icon>
                        </div>
                        <div>
                            <span>Assets</span>
                            <strong>{{ heroCount(summary.usage?.assets) }}</strong>
                            <p>{{ assetsDetail }}</p>
                        </div>
                    </article>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
export default {
    name: 'DashboardHero',
    props: {
        summary: {
            type: Object,
            default: () => ({}),
        },
        storageUsedLabel: {
            type: String,
            default: '',
        },
        heroVisualStyle: {
            type: Object,
            default: () => ({}),
        },
    },
    computed: {
        dashboardRole() {
            return this.summary.dashboardRole || 'none';
        },
        permissions() {
            return this.summary.permissions || {};
        },
        showPlanBadge() {
            return Boolean(this.permissions.canViewBilling);
        },
        planLabel() {
            if (this.dashboardRole === 'team_leader') {
                return 'Operational Dashboard';
            }
            if (this.dashboardRole === 'team_member') {
                return 'Read-only Dashboard';
            }

            return this.summary.plan?.isEnterprise
                ? 'Enterprise Plan'
                : this.summary.plan?.name || 'BYO Dataset Pass';
        },
        chipIcon() {
            return this.dashboardRole === 'owner'
                ? '$vuetify.icons.billing'
                : '$vuetify.icons.teams';
        },
        myTeamCountLabel() {
            const count = Number(this.summary.workspace?.myTeamCount || 0);
            return `${count} ${count === 1 ? 'My Team' : 'My Teams'}`;
        },
        accessTeamCount() {
            return Number(
                this.summary.workspace?.accessTeamCount ||
                    this.summary.usage?.accessTeams ||
                    0
            );
        },
        billingTeamCount() {
            return Number(
                this.summary.workspace?.billingTeamCount ||
                    this.summary.usage?.billingTeams ||
                    this.summary.usage?.seatsUsed ||
                    0
            );
        },
        datasetDetail() {
            return Number(this.summary.workspace?.datasetCount || 0) > 0
                ? this.dashboardRole === 'team_leader'
                    ? `${this.heroCount(this.summary.workspace.datasetCount)} datasets you manage`
                    : this.dashboardRole === 'team_member'
                      ? `${this.heroCount(this.summary.workspace.datasetCount)} datasets available to you`
                      : `${this.heroCount(this.summary.workspace.datasetCount)} published datasets inside groups`
                : 'No datasets yet';
        },
        detailPlanLabel() {
            if (this.dashboardRole === 'team_leader') {
                return 'Operational workspace';
            }
            if (this.dashboardRole === 'team_member') {
                return 'Read-only workspace';
            }
            return this.summary.plan?.name || 'BYO Dataset Pass';
        },
        detailPlanCopy() {
            if (this.dashboardRole === 'team_leader') {
                return 'Upload and manage datasets for teams you lead';
            }
            if (this.dashboardRole === 'team_member') {
                return 'Browse shared datasets, assets, and viewers';
            }
            return 'Bring Your Own storage';
        },
        secondaryMetricLabel() {
            return this.permissions.canViewStorage ? 'Storage' : 'My Teams';
        },
        secondaryMetricValue() {
            return this.permissions.canViewStorage
                ? this.storageUsedLabel || '0.00 GB'
                : this.myTeamCountLabel;
        },
        storageDetail() {
            if (!this.permissions.canViewStorage) {
                return this.dashboardRole === 'team_leader'
                    ? 'Teams where you can manage access'
                    : 'Teams that give you read-only access';
            }
            const usage = this.summary.usage || {};
            if (usage.storageAllowedGb === -1 || usage.storageAllowedBytes === -1) {
                return 'Unlimited storage';
            }
            if (usage.storageAllowedGb === null || usage.storageAllowedBytes === null) {
                return 'Custom storage';
            }
            return 'of your storage';
        },
        teamMetricLabel() {
            return this.permissions.canViewBilling
                ? 'Access Teams'
                : this.dashboardRole === 'team_leader'
                  ? 'Teams You Lead'
                  : 'Teams You Belong To';
        },
        teamMetricValue() {
            return this.permissions.canViewBilling
                ? this.accessTeamCount
                : this.summary.workspace?.myTeamCount || 0;
        },
        accessTeamDetail() {
            const count = this.teamMetricValue;
            return count > 0
                ? this.permissions.canViewBilling
                    ? 'Teams with access'
                    : this.dashboardRole === 'team_leader'
                      ? 'Operational teams you lead'
                      : 'Teams that share data with you'
                : 'No access teams yet';
        },
        billingTeamDetail() {
            if (!this.permissions.canViewBilling) {
                return this.dashboardRole === 'team_leader'
                    ? 'No billing or plan controls in this view'
                    : 'Uploads and billing are hidden in this view';
            }
            const count = this.billingTeamCount;
            return count > 0
                ? `${this.heroCount(count)} Billing Teams - Measured against plan limits`
                : 'No billing teams yet';
        },
        assetsDetail() {
            return Number(this.summary.usage?.assets || 0) > 0
                ? this.permissions.canViewBilling
                    ? 'Asset containers in this billing account'
                    : 'Assets reachable through your team access'
                : 'No assets yet';
        },
        heroActions() {
            const actions = [];
            const manageTeamsUrl =
                this.summary.links?.manageTeamsUrl ||
                this.summary.teamPanel?.manageUrl ||
                '/teams/';

            if (this.permissions.canUpload && this.summary.links?.upload) {
                actions.push({
                    label: 'Upload Dataset',
                    href: this.summary.links.upload,
                    icon: '$vuetify.icons.upload',
                    primary: true,
                });
            }

            actions.push({
                label: 'Open Viewer',
                href: this.summary.links?.viewer || '/view',
                icon: '$vuetify.icons.eye',
                primary: !actions.length,
            });

            actions.push({
                label:
                    this.dashboardRole === 'team_member'
                        ? 'My Teams'
                        : 'Manage Access',
                href: manageTeamsUrl,
                icon:
                    this.dashboardRole === 'team_member'
                        ? '$vuetify.icons.teams'
                        : '$vuetify.icons.teams',
                primary: false,
            });

            return actions;
        },
    },
    methods: {
        heroCount(value, fallback = 0) {
            const parsed = Number(value ?? fallback);
            const safeValue = Number.isFinite(parsed) ? parsed : fallback;
            return new Intl.NumberFormat('en-US', {
                maximumFractionDigits: 0,
            }).format(safeValue);
        },
    },
};
</script>

<style scoped>
.workspace-dashboard__hero {
    display: grid;
    grid-template-columns: minmax(0, 1.36fr) minmax(420px, 0.94fr);
    min-height: clamp(232px, 18vw, 280px);
    overflow: hidden;
    border: 1px solid rgba(229, 233, 244, 0.96);
    border-radius: 24px;
    background: #ffffff;
    box-shadow:
        0 20px 46px rgba(16, 24, 40, 0.07),
        0 2px 5px rgba(16, 24, 40, 0.05);
}

.workspace-dashboard__hero-copy {
    display: grid;
    grid-template-columns: minmax(390px, 1.4fr) minmax(270px, 0.86fr);
    align-items: stretch;
    gap: clamp(16px, 1.65vw, 26px);
    padding: clamp(18px, 1.7vw, 26px);
}

.workspace-dashboard__hero-intro {
    display: grid;
    align-content: center;
    gap: 13px;
    min-width: 0;
}

.workspace-dashboard__hero-topline {
    display: flex;
    align-items: center;
    gap: 14px;
    min-width: 0;
}

.workspace-dashboard__hero-teammark {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 8px;
    background: linear-gradient(180deg, #eef3ff 0%, #edf4ff 100%);
    color: #2f6df8;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.8);
}

.workspace-dashboard__eyebrow {
    display: flex;
    align-items: center;
    gap: 10px;
    color: #8b93a7;
    font-size: 0.75rem;
    font-weight: 500;
    letter-spacing: 0;
    text-transform: uppercase;
    min-width: 0;
}

.workspace-dashboard__separator {
    opacity: 0.7;
}

.workspace-dashboard__hero h1 {
    margin: 0;
    color: #193059;
    font-size: clamp(1.82rem, 2.05vw, 2.18rem);
    font-weight: 700;
    line-height: 1.06;
    letter-spacing: 0;
}

.workspace-dashboard__hero-wave {
    display: inline-block;
    margin-left: 8px;
    font-size: 0.75em;
    transform: translateY(-0.02em);
}

.workspace-dashboard__hero p {
    margin: 0;
    color: #8690a8;
    font-size: 0.95rem;
    line-height: 1.45;
}

.workspace-dashboard__hero-badges,
.workspace-dashboard__hero-actions,
.workspace-dashboard__hero-glance {
    display: grid;
    gap: 16px;
}

.workspace-dashboard__hero-badges {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
}

.workspace-dashboard__hero-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-height: 30px;
    padding: 0 12px;
    border: 1px solid #d6deea;
    border-radius: 999px;
    background: #f8fbff;
    color: #4d5871;
    box-shadow: 0 1px 0 rgba(255, 255, 255, 0.78) inset;
}

.workspace-dashboard__hero-badge strong {
    font-size: 0.82rem;
    font-weight: 600;
}

.workspace-dashboard__hero-badge--primary {
    border-color: rgba(53, 101, 245, 0.2);
    background: rgba(53, 101, 245, 0.06);
    color: #2f63e8;
}

.workspace-dashboard__hero-badge--success {
    border-color: rgba(16, 154, 107, 0.18);
    background: rgba(16, 154, 107, 0.08);
    color: #168f67;
}

.workspace-dashboard__hero-details {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    align-items: stretch;
    min-width: 0;
}

.workspace-dashboard__hero-detail {
    display: grid;
    align-content: center;
    gap: 6px;
    min-width: 0;
    min-height: 114px;
    padding: 0 clamp(14px, 1.35vw, 22px);
    border-left: 1px solid #e6ebf3;
}

.workspace-dashboard__hero-glance-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: 42px;
    height: 42px;
    border-radius: 50%;
}

.workspace-dashboard__hero-glance-icon--blue {
    background: #eff4ff;
    color: #3565f5;
}

.workspace-dashboard__hero-glance-icon--green {
    background: #e8fff5;
    color: #139d71;
}

.workspace-dashboard__hero-glance-icon--purple {
    background: rgba(112, 76, 248, 0.12);
    color: #704cf8;
}

.workspace-dashboard__hero-glance-icon--orange {
    background: rgba(247, 150, 52, 0.14);
    color: #f79634;
}

.workspace-dashboard__hero-detail span,
.workspace-dashboard__hero-glance-card span {
    color: #8b93a7;
    font-size: 0.72rem;
    letter-spacing: 0;
    text-transform: uppercase;
}

.workspace-dashboard__hero-detail strong {
    color: #173057;
    font-size: clamp(0.98rem, 1.08vw, 1.12rem);
    font-weight: 700;
    letter-spacing: 0;
    line-height: 1.18;
}

.workspace-dashboard__hero-detail p,
.workspace-dashboard__hero-glance-card p {
    margin: 0;
    color: #8690a8;
    font-size: 0.82rem;
    line-height: 1.35;
}

.workspace-dashboard__hero-glance-card small {
    color: #cdd7e7;
    font-size: 0.75rem;
    line-height: 1.25;
}

.workspace-dashboard__hero-actions {
    grid-template-columns: repeat(3, minmax(0, max-content));
    justify-content: start;
    gap: 12px;
    margin-top: 18px;
}

.workspace-dashboard__hero-action {
    min-width: 138px;
    min-height: 42px;
    padding-inline: 18px;
    border-color: #bcc5d6;
    border-radius: 6px;
    color: #203553;
    font-size: 0.86rem;
    font-weight: 600;
    text-transform: none;
    letter-spacing: 0;
}

.workspace-dashboard__hero-action--primary {
    border-color: #285ff6;
    background: #285ff6;
    color: #ffffff;
    box-shadow: 0 10px 24px rgba(40, 95, 246, 0.28);
}

.workspace-dashboard__hero-visual {
    position: relative;
    min-height: 100%;
    background-color: #102c45;
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
}

.workspace-dashboard__hero-visual::before {
    content: "";
    position: absolute;
    inset: 0;
    background:
        linear-gradient(180deg, rgba(7, 24, 43, 0.08), rgba(7, 24, 43, 0.66)),
        radial-gradient(circle at 30% 18%, rgba(88, 215, 255, 0.16), transparent 38%);
    pointer-events: none;
}

.workspace-dashboard__hero-visual-overlay {
    position: relative;
    z-index: 1;
    display: grid;
    grid-template-rows: auto 1fr;
    gap: 14px;
    height: 100%;
    padding: 16px;
    background:
        linear-gradient(180deg, rgba(8, 24, 42, 0.1), rgba(8, 24, 42, 0.26));
}

.workspace-dashboard__hero-chip {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    justify-self: end;
    padding: 7px 14px;
    border-radius: 999px;
    background: rgba(228, 255, 243, 0.92);
    color: #178b64;
    font-size: 0.8rem;
    font-weight: 700;
    letter-spacing: 0;
    text-transform: uppercase;
}

.workspace-dashboard__hero-chip :deep(.v-icon) {
    color: inherit;
}

.workspace-dashboard__hero-glance {
    align-content: end;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    width: 100%;
    justify-self: stretch;
    align-self: end;
    margin-top: auto;
}

.workspace-dashboard__hero-glance-card {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
    min-height: 82px;
    padding: 12px;
    border: 1px solid rgba(201, 221, 237, 0.26);
    border-radius: 6px;
    background: rgba(25, 40, 55, 0.68);
    backdrop-filter: blur(12px);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08);
}

.workspace-dashboard__hero-glance-card > div:last-child {
    display: grid;
    gap: 3px;
    min-width: 0;
}

.workspace-dashboard__hero-glance-card strong {
    color: #ffffff;
    font-size: clamp(1.1rem, 1.35vw, 1.45rem);
    font-weight: 700;
    line-height: 1;
    letter-spacing: 0;
    white-space: nowrap;
}

.workspace-dashboard__hero-glance-card p {
    color: rgba(255, 255, 255, 0.82);
    overflow-wrap: anywhere;
}

html.theme--dark .workspace-dashboard__hero,
body.theme--dark .workspace-dashboard__hero,
.theme--dark .workspace-dashboard__hero,
.v-theme--dark .workspace-dashboard__hero {
    border-color: var(--sd-dark-border);
    background: var(--sd-dark-surface-panel);
    box-shadow: 0 20px 46px rgba(4, 10, 18, 0.36);
}

html.theme--dark .workspace-dashboard__hero-copy,
body.theme--dark .workspace-dashboard__hero-copy,
.theme--dark .workspace-dashboard__hero-copy,
.v-theme--dark .workspace-dashboard__hero-copy {
    background: var(--sd-dark-surface-panel);
}

html.theme--dark .workspace-dashboard__hero h1,
body.theme--dark .workspace-dashboard__hero h1,
.theme--dark .workspace-dashboard__hero h1,
.v-theme--dark .workspace-dashboard__hero h1,
html.theme--dark .workspace-dashboard__hero-detail strong,
body.theme--dark .workspace-dashboard__hero-detail strong,
.theme--dark .workspace-dashboard__hero-detail strong,
.v-theme--dark .workspace-dashboard__hero-detail strong,
html.theme--dark .workspace-dashboard__hero-glance-card strong,
body.theme--dark .workspace-dashboard__hero-glance-card strong,
.theme--dark .workspace-dashboard__hero-glance-card strong,
.v-theme--dark .workspace-dashboard__hero-glance-card strong {
    color: var(--sd-dark-text-inverse);
}

html.theme--dark .workspace-dashboard__hero p,
body.theme--dark .workspace-dashboard__hero p,
.theme--dark .workspace-dashboard__hero p,
.v-theme--dark .workspace-dashboard__hero p,
html.theme--dark .workspace-dashboard__hero-detail span,
body.theme--dark .workspace-dashboard__hero-detail span,
.theme--dark .workspace-dashboard__hero-detail span,
.v-theme--dark .workspace-dashboard__hero-detail span,
html.theme--dark .workspace-dashboard__hero-glance-card span,
body.theme--dark .workspace-dashboard__hero-glance-card span,
.theme--dark .workspace-dashboard__hero-glance-card span,
.v-theme--dark .workspace-dashboard__hero-glance-card span,
html.theme--dark .workspace-dashboard__hero-badge,
body.theme--dark .workspace-dashboard__hero-badge,
.theme--dark .workspace-dashboard__hero-badge,
.v-theme--dark .workspace-dashboard__hero-badge {
    color: var(--sd-dark-text-soft);
}

html.theme--dark .workspace-dashboard__hero-teammark,
body.theme--dark .workspace-dashboard__hero-teammark,
.theme--dark .workspace-dashboard__hero-teammark,
.v-theme--dark .workspace-dashboard__hero-teammark {
    background: var(--sd-dark-surface-control);
    color: var(--sd-dark-text-inverse);
}

html.theme--dark .workspace-dashboard__hero-badge,
body.theme--dark .workspace-dashboard__hero-badge,
.theme--dark .workspace-dashboard__hero-badge,
.v-theme--dark .workspace-dashboard__hero-badge {
    border-color: var(--sd-dark-border-medium);
    background: var(--sd-dark-surface-control);
}

html.theme--dark .workspace-dashboard__hero-badge--primary,
body.theme--dark .workspace-dashboard__hero-badge--primary,
.theme--dark .workspace-dashboard__hero-badge--primary,
.v-theme--dark .workspace-dashboard__hero-badge--primary {
    border-color: rgba(53, 101, 245, 0.35);
    background: rgba(53, 101, 245, 0.16);
    color: #a9c0ff;
}

html.theme--dark .workspace-dashboard__hero-badge--success,
body.theme--dark .workspace-dashboard__hero-badge--success,
.theme--dark .workspace-dashboard__hero-badge--success,
.v-theme--dark .workspace-dashboard__hero-badge--success {
    border-color: rgba(16, 154, 107, 0.28);
    background: rgba(16, 154, 107, 0.18);
    color: #a5f0cc;
}

html.theme--dark .workspace-dashboard__hero-detail,
body.theme--dark .workspace-dashboard__hero-detail,
.theme--dark .workspace-dashboard__hero-detail,
.v-theme--dark .workspace-dashboard__hero-detail {
    border-color: rgba(255, 255, 255, 0.08);
}

html.theme--dark .workspace-dashboard__hero-action,
body.theme--dark .workspace-dashboard__hero-action,
.theme--dark .workspace-dashboard__hero-action,
.v-theme--dark .workspace-dashboard__hero-action {
    border-color: var(--sd-dark-border-medium);
    color: var(--sd-dark-text-inverse);
}

html.theme--dark .workspace-dashboard__hero-action--primary,
body.theme--dark .workspace-dashboard__hero-action--primary,
.theme--dark .workspace-dashboard__hero-action--primary,
.v-theme--dark .workspace-dashboard__hero-action--primary {
    border-color: #285ff6;
    color: #ffffff;
}

html.theme--dark .workspace-dashboard__hero-visual-overlay,
body.theme--dark .workspace-dashboard__hero-visual-overlay,
.theme--dark .workspace-dashboard__hero-visual-overlay,
.v-theme--dark .workspace-dashboard__hero-visual-overlay {
    background:
        linear-gradient(180deg, rgba(8, 24, 42, 0.18), rgba(8, 24, 42, 0.36));
}

html.theme--dark .workspace-dashboard__hero-chip,
body.theme--dark .workspace-dashboard__hero-chip,
.theme--dark .workspace-dashboard__hero-chip,
.v-theme--dark .workspace-dashboard__hero-chip {
    background: rgba(12, 36, 29, 0.92);
    color: #a7f0ce;
}

html.theme--dark .workspace-dashboard__hero-glance-card,
body.theme--dark .workspace-dashboard__hero-glance-card,
.theme--dark .workspace-dashboard__hero-glance-card,
.v-theme--dark .workspace-dashboard__hero-glance-card {
    border-color: rgba(255, 255, 255, 0.08);
    background: rgba(12, 18, 28, 0.72);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.05);
}

html.theme--dark .workspace-dashboard__hero-glance-card p,
body.theme--dark .workspace-dashboard__hero-glance-card p,
.theme--dark .workspace-dashboard__hero-glance-card p,
.v-theme--dark .workspace-dashboard__hero-glance-card p {
    color: rgba(238, 244, 255, 0.82);
}

html.theme--dark .workspace-dashboard__hero-glance-card small,
body.theme--dark .workspace-dashboard__hero-glance-card small,
.theme--dark .workspace-dashboard__hero-glance-card small,
.v-theme--dark .workspace-dashboard__hero-glance-card small {
    color: rgba(238, 244, 255, 0.6);
}

@media (max-width: 1320px) {
    .workspace-dashboard__hero {
        grid-template-columns: minmax(0, 1fr);
        min-height: auto;
    }

    .workspace-dashboard__hero-visual {
        min-height: 300px;
    }

}

@media (max-width: 1080px) {
    .workspace-dashboard__hero-copy,
    .workspace-dashboard__hero-details,
    .workspace-dashboard__hero-badges,
    .workspace-dashboard__hero-actions {
        grid-template-columns: 1fr;
    }

    .workspace-dashboard__hero-details {
        gap: 12px;
    }

    .workspace-dashboard__hero-detail {
        min-height: auto;
        padding: 14px 0 0;
        border-top: 1px solid #e6ebf3;
        border-left: 0;
    }

    .workspace-dashboard__hero-glance {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .workspace-dashboard__hero-copy {
        padding: 22px;
    }

    .workspace-dashboard__hero-glance-card {
        min-height: auto;
    }
}

@media (max-width: 760px) {
    .workspace-dashboard__hero-copy {
        gap: 16px;
    }

    .workspace-dashboard__hero h1 {
        font-size: clamp(1.9rem, 9vw, 2.7rem);
    }

    .workspace-dashboard__hero-glance-card {
        padding: 16px;
    }

    .workspace-dashboard__hero-actions {
        grid-template-columns: 1fr;
    }

    .workspace-dashboard__hero-glance {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .workspace-dashboard__hero-visual-overlay {
        padding: 16px;
    }
}
</style>
